import { useState, useEffect, useRef } from 'react';
import { usePiViewer, STATUS } from '../WEBRTC/PiViewer';
import s from '../../Assets/Styles/Joystick/CameraView.module.css';

// Same "LIVE CAMERA" panel as before, but now powered by the KVS WebRTC
// connection (usePiViewer) instead of an <iframe> pointed at a manual
// local IP. `ip` is no longer used — the KVS channel isn't reached by IP
// address — so it's been dropped from the props; `connected` (MQTT
// online/offline) still drives when the panel tries to connect, same as
// the old "auto-set IP from robot" behavior did.
export default function CameraView({ connected }) {
    const [clock, setClock] = useState('');

    const {
        videoRef,
        status,
        errorMessage,
        isWatching,
        startViewing,
        stopViewing,
    } = usePiViewer();

    useEffect(() => {
        const id = setInterval(() =>
            setClock(new Date().toLocaleTimeString('en-GB', { hour12: false })), 1000);
        return () => clearInterval(id);
    }, []);

    // Auto-start the stream once MQTT reports the robot online, and stop
    // it again if MQTT drops.
    const wasConnectedRef = useRef(false);
    useEffect(() => {
        if (connected && !wasConnectedRef.current) {
            startViewing();
        } else if (!connected && wasConnectedRef.current) {
            stopViewing();
        }
        wasConnectedRef.current = connected;
    }, [connected, startViewing, stopViewing]);

    const isLive = status === STATUS.LIVE;
    const isReconnecting = status === STATUS.RECONNECTING;
    const isConnecting = status === STATUS.CONNECTING;

    // Centered text/overlay shown on top of the video for any non-live
    // state. Inline styles on purpose — this overlay didn't exist in the
    // original design (which only ever showed the placeholder OR the
    // stream, never layered), so it can't safely borrow .placeholder's
    // CSS without knowing whether that class is positioned for overlay use.
    const overlayStyle = {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.4rem',
        background: 'rgba(0, 0, 0, 0.35)',
        color: '#fff',
        textAlign: 'center',
        padding: '0 1rem',
    };

    return (
        <div className={s.wrap}>
            {/* ── Header ── */}
            <div className={s.head}>
                <span className={s.title}>📷 LIVE CAMERA</span>
                <div className={s.headRight}>
                    {isWatching && (
                        <span className={s.ipBadge}>
                            <span className={s.ipPulse} />
                            {isReconnecting ? 'Reconnecting…' : isLive ? 'Live' : 'Connecting…'}
                        </span>
                    )}
                    <span className={s.hudTime}>{clock}</span>
                </div>
            </div>

            {/* ── Video Viewport ── */}
            <div className={s.viewport} style={{ position: 'relative' }}>
                {!connected ? (
                    <div className={s.placeholder}>
                        <div className={s.phIcon}>📡</div>
                        <div className={s.phText}>CONNECT MQTT</div>
                        <div className={s.phSub}>Camera connects once the robot handshake completes</div>
                    </div>
                ) : (
                    <>
                        {/* Always mounted once MQTT is connected, so videoRef
                        exists by the time the WebRTC stream attaches to it —
                        just dimmed while reconnecting, covered by the overlay
                        below otherwise. */}
                        <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            className={s.stream}
                            style={isReconnecting ? { filter: 'grayscale(60%) brightness(55%)' } : undefined}
                        />

                        {!isLive && (
                            <div style={overlayStyle}>
                                <div className={s.phIcon}>{isReconnecting ? '🔄' : '⏳'}</div>
                                <div className={s.phText}>
                                    {isReconnecting ? 'RECONNECTING…' : isConnecting ? 'CONNECTING…' : 'AWAITING STREAM'}
                                </div>
                                <div className={s.phSub}>
                                    {isReconnecting ? 'Showing last frame' : (errorMessage || 'Establishing the camera link')}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* REC overlay — only when actually live */}
                {isLive && (
                    <div className={s.hud}>
                        <span className={s.rec}>● REC</span>
                    </div>
                )}
            </div>
        </div>
    );
}